import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import CustomTh from '../../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../../components/table/CustomTd';
import TableFunction from '../../../../../../../components/table/TableFunction';
import CustomChip from '../../../../../../../components/CustomChip';
import { colors } from '../../../../../../../enums/colors';
import {
    EMPTY_VALUES, errorMsg, getDateFormat
} from '../../../../../../../Apis/functions';
import {
    ATTENDANCE_COLOR_MODEL, ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE
} from '../../../Model/Model';
import { GetAttendanceColor } from '../../../../../../../reduxToolkit/AttendanceConfig/AttendanceConfigApi';
import {DEFAULT_LEFT, SEARCH_MODEL} from "../../../../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../../../../reduxToolkit/Search/SearchApi";
import {
    GetAttendanceAdjustmentGroupById,
    GetAttendancePeriodReview,
    ValidateAttendanceAdjustmentDate
} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";
import {useLocation, useNavigate} from "react-router-dom";
import CustomDateTimePicker from "../../../../../../../components/CustomDateTimePicker";
import {hasAdjustedChanges, hasInvalidAdjustedRows, ROW_ATTENDANCE_ADJUSTED} from "../Model/Model";
import CommonPeriodReviewTableApp from "./CommonPeriodReviewTableApp";
import {TABLES} from "../../../../../../../Apis/Tables";
import routes from "../../../../../../../Routes/routes";
import {SUBMISSION_REQUEST_CARD_MODEL} from "../../Request/Model/Model";
import {SaveRowsAdjusted} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentPersistSlice";
import ListDisplayWithTooltip from "../../../../../../../components/ListDisplayWithTooltip";
import {IconPlusFill, PersonOutlinedChip} from "../../../../../../../components/buttons/IconButtons";
import {DEVICE_ACCESS_TYPE_MODEL} from "../../../../../Devices/Model/Model";
import {ErrorCode} from "../../../../../../../Apis/ErrorCode";
/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : FullStack Developer
 * @Email : kpozos@ibl.mx
 *
 */
const ChangeRequestTableApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation(); // React Router hook for accessing current route
    const {requestId} = location.state;   // Access route state (e.g. ID passed from previous page)

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const {getAttendancePeriodReview} = useSelector((state) => state.AttendanceAdjustmentSlice);
    const { adjustedChangeRequest, adjustedChangeRequestId } = useSelector((state) => state.AttendanceAdjustmentPersistSlice);
    const { isAttendanceSupervisor } = useSelector((state) => state.AttendancePersistSlice);
    const tableId = "tbl_crt_changes_requested"; // Set the table ID for identification
    const [results, setResults] = useState({ content: [], totalElements: 0 }); // Store the search results
    const leftPosition = DEFAULT_LEFT;
    const [color, setColor] = useState({ ...ATTENDANCE_COLOR_MODEL.setDefaultData() });
    const option = TABLES.ATTENDANCE_SUBMISSION_CHANGE_REQUEST;
    const [item, setItem] = useState(null);
    const badge = SUBMISSION_REQUEST_CARD_MODEL.getById(item?.approvalStatusId);
    const [rowsAdjusted, setRowsAdjusted]= useState(null);
    // The draft is merged once the validation answers, so the rows are read from a ref
    // to avoid merging over a state captured before the request started.
    const rowsAdjustedRef = useRef(rowsAdjusted);
    // The scheduled shift column is fixed next to the name one, so it starts where
    // the name column ends. That width is only known once the header is rendered.
    const nameHeadRef = useRef(null);
    const [scheduledLeft, setScheduledLeft] = useState(DEFAULT_LEFT);
    const disableReviewUpdatesBtn =  !rowsAdjusted ||
        hasInvalidAdjustedRows(rowsAdjusted) ||
        !hasAdjustedChanges(rowsAdjusted);

    /**
     * Saves a draft version of an adjusted row.
     *
     * The draft only lives in Redux, so the date is sent to the backend first: the
     * scheduled shift rules live there and a value they reject must not reach the
     * request. Only a validated date is merged into `rowsAdjusted`.
     *
     * @param {Object} row - Adjusted row data
     * @param {number|string} row.attendanceId - Unique identifier for the attendance record
     * @param {string} adjustmentType - Type of adjustment (ENTRY / EXIT)
     */
    const saveDraft = (row, adjustmentType) => {
        const toValidate = ROW_ATTENDANCE_ADJUSTED.toRegister(row, adjustmentType);
        dispatch(ValidateAttendanceAdjustmentDate(toValidate)).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                const updated = {
                    ...rowsAdjustedRef.current,
                    [row.attendanceId]: row
                };
                setRowsAdjusted(updated);
                dispatch(SaveRowsAdjusted({ rows: updated, requestId }));
                return;
            }

            // The allowed range comes from the response: the backend widens the
            // scheduled shift with its tolerance windows, so the row bounds would name
            // a narrower range than the one it accepts.
            errorMsg(code, {
                from: getDateFormat(data?.from),
                to: getDateFormat(data?.to)
            });
            const isEntry = adjustmentType === DEVICE_ACCESS_TYPE_MODEL.ENTRY.name;

            // A date outside the scheduled shift stays in the picker with the reason
            // under it, so the user can correct it without picking again from scratch.
            if (code === ErrorCode.ATTENDANCE_ADJUSTMENT_OUT_OF_SHIFT) {
                setRowsAdjusted(prev => ({
                    ...prev,
                    [row.attendanceId]: {
                        ...row,
                        ...(isEntry
                            ? {
                                entryFullAdjustedIsValid: false,
                                entryInvalidText: 'outside_scheduled_shift_short'
                            }
                            : {
                                exitFullAdjustedIsValid: false,
                                exitInvalidText: 'outside_scheduled_shift_short'
                            })
                    }
                }));
                return;
            }

            // Any other rejection restores the previous value. The picker holds the
            // rejected one internally and a same-valued prop does not clear it, so its
            // reset version is bumped to remount it.
            setRowsAdjusted(prev => {
                const previous = prev?.[row.attendanceId] || {};

                return {
                    ...prev,
                    [row.attendanceId]: {
                        ...previous,
                        entryResetVersion: (previous.entryResetVersion || 0) + (isEntry ? 1 : 0),
                        exitResetVersion: (previous.exitResetVersion || 0) + (isEntry ? 0 : 1),
                    }
                };
            });
        });
    };

    useEffect(() => {
        rowsAdjustedRef.current = rowsAdjusted;
    }, [rowsAdjusted]);

    const {
        entryFullOnChange,
        exitFullOnChange,
        getTotalTime,
        getGeneralStatus,
        getEntryExitColumn,
        getScheduledColumn
    } = CommonPeriodReviewTableApp({
        setRowsAdjusted,
        color,
        saveDraft,
        // Every pick is checked through group/validate-date, so the shift rules are
        // answered by the backend.
        validateOutOfShift: false
    });

    /**
     * Initializes required data on component mount.
     *
     * Flow:
     * 1. Fetches attendance period review data
     * 2. Loads attendance color configuration
     * 3. Retrieves adjustment group details by requestId
     *
     * Notes:
     * - Executes only once on mount
     * - Depends on requestId from route state
     */
    useEffect(() => {
        dispatch(GetAttendancePeriodReview()).then(({ payload: { data: { data, success } } }) => {
            if (success && data){
                dispatch(GetAttendanceColor()).then(
                    ({
                         payload: {
                             data: { data },
                         },
                     }) => {
                        setColor({ ...ATTENDANCE_COLOR_MODEL, ...data });
                    },
                );

                dispatch(GetAttendanceAdjustmentGroupById(requestId)).then(
                    ({
                         payload: {
                             data: { data },
                         },
                     }) => {
                        setItem(data);
                    },
                );
            }
        });
    }, []);

    /**
     * Generates table header configuration.
     *
     * Defines column structure for:
     * - Employee info
     * - Entry/exit captured data
     * - Adjusted values
     * - Status indicators
     * - Total time
     *
     * @returns {Array<JSX.Element>} Table header cells
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes='first_head' headRef={nameHeadRef}>
                {t('name')}
            </CustomTh>,
            <CustomTh key={`th_crt_scheduled_${scheduledLeft}`} tableId={tableId} fixedXY={true} left={scheduledLeft}>
                {t('scheduled_shift')}
            </CustomTh>,
            <CustomTh tableId={tableId}>{t('supervisor')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_captured')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('new_entry_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_captured')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('new_exit_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('total_time')}</CustomTh>,
        ];
    };

    /**
     * Generates table body rows based on search results.
     *
     * Behavior:
     * - Maps attendance records into table rows
     * - Merges persisted adjustments (`rowsAdjusted`)
     * - Handles disabled state per row
     * - Renders:
     *    - Entry/exit captured values
     *    - Editable date-time pickers
     *    - Status indicators
     *    - Total time calculation
     *
     * @returns {Object}
     * @returns {Array<JSX.Element>} returns.body - Rendered table rows
     * @returns {Array<Object>} returns.data - Raw data used for rendering
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0)
            return {
                body: [],
                data: [],
            };

        let body = results?.content.map((item, index) => {
            const current = rowsAdjusted?.[item.attendanceId] || {};
            const disabled = current?.disabled || false;
            const exitFullAdjusted = current?.exitFullAdjusted || null;

            const entryStatusAdjusted = current?.entryStatusAdjusted || null;
            const exitStatusAdjusted = current?.exitStatusAdjusted || null;
            // Entry and exit can be adjusted by the same person, so the name is
            // only listed once when both match.
            const supervisors = [...new Set(
                [item?.entryDateAdjustedBy, item?.exitDateAdjustedBy].filter(Boolean)
            )].map(fullName => ({ fullName }));

            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={'first-column py-3'}>
                        <p className={'mb-1'}>{item?.fullName}</p>

                        { item?.departmentName &&
                            <CustomChip
                                background={colors.secondary100}
                                fontColor={colors.secondary700}
                                borderColor={colors.secondary150}
                                legend={item?.departmentName }
                                variant={'compact'}
                            />
                        }
                    </CustomTd>

                    <CustomTd key={`td_crt_scheduled_${item?.attendanceId}_${scheduledLeft}`} tableId={tableId} fixed={true} left={scheduledLeft} classes={"py-3"}>
                        { getScheduledColumn(item.shiftStart, item.shiftEnd) }
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        <ListDisplayWithTooltip
                            items={supervisors}
                            renderItem={(u) => u.fullName}
                            maxInline={2}
                            emptyValue={EMPTY_VALUES.DEFAULT}
                            icon={<IconPlusFill width={20} height={20} />}
                            itemIcon={<PersonOutlinedChip className="icon-color-general-950" />}
                        />
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getEntryExitColumn(item.entryDeviceName, item.entryDateOriginal, item.entryAccessMethodId, item.attendanceEntryStatusId)}
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        <div className={"w-272px"}>
                            <CustomDateTimePicker
                                key={`dp_crt_entry_date_adjusted_${item?.attendanceId}_${current?.entryResetVersion || 0}`}
                                id={`dp_crt_entry_date_adjusted_${item?.attendanceId}_${index}`}
                                name={'entryFullAdjusted'}
                                value={current?.entryFullAdjusted}
                                disabled={disabled}
                                onAccept={(e) => entryFullOnChange(e, current, item)}
                                noMarginBottom={true}
                                onlySelecting={true}
                                isValid={current.entryFullAdjustedIsValid}
                                isValidText={current.entryInvalidText || "date_invalid"}
                                minutesStep={1}
                                secondsStep={1}
                            />
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getGeneralStatus(item.attendanceEntryStatusId, entryStatusAdjusted, item?.entryDateAdjustedBy, disabled) }
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getEntryExitColumn(item.exitDeviceName, item.exitDateOriginal, item.exitAccessMethodId, item.attendanceExitStatusId)}
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <div className={"w-272px"}>
                            <CustomDateTimePicker
                                key={`dp_crt_exit_date_adjusted_${item?.attendanceId}_${current?.exitResetVersion || 0}`}
                                id={`dp_crt_exit_date_adjusted_${item?.attendanceId}_${index}`}
                                name={'exitFullAdjusted'}
                                value={exitFullAdjusted}
                                disabled={disabled}
                                onAccept={(e) => exitFullOnChange(e, current, item)}
                                noMarginBottom={true}
                                onlySelecting={true}
                                isValid={current.exitFullAdjustedIsValid}
                                isValidText={current.exitInvalidText || "date_invalid"}
                            />
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getGeneralStatus(item.attendanceExitStatusId, exitStatusAdjusted, item?.exitDateAdjustedBy, disabled) }
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        {getTotalTime(item, current)}
                    </CustomTd>
                </>
            );
        });

        return {
            body: body,
            data: results?.content,
        };
    };

    /**
     * Fetches paginated attendance data for the table.
     *
     * Behavior:
     * - Builds search request payload
     * - Executes search via Redux action
     * - Initializes adjustment rows if not already present
     * - Syncs results with local and global (Redux) state
     *
     * @param {number} page - Current page index
     * @param {Object} params - Search parameters
     * @param {string} params.searchElement - Search query string
     */
    const getData = (page, { searchElement }) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size:rowsPerPage,
            model:option,
            key:searchElement,
            modelId: requestId
        })
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    // Rows kept from another change request would send item ids that
                    // do not belong to this one, so they are dropped.
                    const persisted = adjustedChangeRequestId === requestId ? adjustedChangeRequest : null;
                    const initial = {...persisted};

                    data.content.forEach(item => {
                        if(!initial[item.attendanceId]){
                            initial[item.attendanceId] = ROW_ATTENDANCE_ADJUSTED.toSet(item, false);
                        }
                    });
                    setResults(data);
                    setRowsAdjusted(initial);
                    dispatch(SaveRowsAdjusted({ rows: initial, requestId }));
                }
            },
        );
    };

    const { t, thead, tbody, pagination, rowsPerPage } = TableFunction({
        getTbody: getTbody,
        getThead: getThead,
        data: results,
        labelRowsPerPage: 'employees_per_page',
        hasContainer: true,
        fixedPagination: false,

        getData,//New
        dependencies: [rowsAdjusted, color, scheduledLeft]
    });

    /**
     * Keeps the scheduled shift column pinned right after the name one.
     *
     * The name column has no fixed width, so its rendered width is measured and
     * used as the left offset of the scheduled column.
     */
    useEffect(() => {
        // The header cell fills the ref on its own effect, so the width is read
        // after paint. Without a measure both columns would stick at 0 and the
        // scheduled one would cover the name.
        const updateScheduledLeft = () => {
            const width = nameHeadRef.current?.offsetWidth;
            if (width) setScheduledLeft(width);
        };

        const frame = requestAnimationFrame(updateScheduledLeft);
        window.addEventListener('resize', updateScheduledLeft);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', updateScheduledLeft);
        };
    }, [thead, results]);

    /**
     * Navigates back to the main attendance submissions view.
     *
     * Preserves navigation state:
     * - Active tab
     * - Current subview (Requested)
     */
    const handleOnBack = () => {
        navigate(routes.attendance.main, {
            state: {
                currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED
            },
        });
    };

    /**
     * Navigates to the confirmation screen for reviewing changes.
     *
     * Passes:
     * - Current tab and subview
     * - Request identifier for processing
     */
    const handleReviewUpdates = () => {
        navigate(routes.submission.confirmChangeRequest, {
            state: {
                currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED,
                requestId: requestId
            },
        });
    };

    return {
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        tableId,
        thead,
        tbody,
        pagination,
        getAttendancePeriodReview,
        handleOnBack,
        badge,
        item,
        handleReviewUpdates,
        disableReviewUpdatesBtn,
        isAttendanceSupervisor
    };
};

export default ChangeRequestTableApp;
